import { Injectable, inject } from '@angular/core';
import { StaticApplicationId, UserService } from 'projects/lib-shared-core/src/public-api';
import { SvcModalActionPlanStatusTypeById } from '../enums/svc-modal-action-plan-status.enum';
import { SvcActionPlanEscalationHistory } from '../interfaces/svc-modal-action-plan.interface';

/**
 * Minimal shape used by every permission check on this service. All fields are
 * optional because different API responses (search list, full action plan,
 * kanban item) populate different subsets of them.
 */
export interface ActionPlanPermissionShape {
  originatorUserId?: string | null;
  responsibleUserId?: string | null;
  /** Users authorised to approve (multi). Drives `canApprove`/`hasAssignedApprover`. */
  approverUsers?: string[] | null;
  /**
   * User who actually approved (single, set server-side). Kept for response
   * parity / audit display — NOT used by the permission rules below.
   */
  approverUserId?: string | null;

  // "actors" / "owners" — used by canEdit / canFinalize / canReschedule
  actorUserIds?: string[];
  actorsUserIds?: string[];
  actors?: string[];
  userOwners?: string[];
  teamOwners?: number[] | null;

  // "participants" — used by canEscalate
  participantUserIds?: string[];
  participantsUserIds?: string[];
  participants?: string[];

  // status (used by checklist rules — concluded plans freeze the tab)
  statusId?: number | null;

  // system lock flags (escalate flow)
  isSystemInternal?: boolean;
  isSystemLockedForEdit?: boolean;
  lockSystemOnEdit?: boolean;
  sourceApplicationId?: string | null;

  /**
   * Origin site of the plan (`siteId` on `GET /ActionPlan/{id}`). Escalation
   * never rewrites it, so it is the authoritative origin for the site gate —
   * see {@link SvcActionPlanPermissionService.isSiteRestrictedAfterEscalation}.
   */
  siteId?: number | null;
  /**
   * Escalation trail (full getActionPlanById payload only). When the plan was
   * escalated to another site, write permissions stay scoped to the origin
   * site — see {@link SvcActionPlanPermissionService.isSiteRestrictedAfterEscalation}.
   * List/kanban shapes don't carry it, so those consumers keep the plain matrix.
   */
  escalationHistories?: SvcActionPlanEscalationHistory[] | null;
  hostApplicationId?: string | null;
}

/**
 * Centralised action-plan permission rules.
 *
 * Lives in `lib-shared-feature/modals/svc-modal-action-plan/` so the modal,
 * the kanban-board, the search page, the escalate/reschedule services, and
 * any future consumer can share **a single source of truth**.
 *
 * The matrix:
 *   - canEdit / canFinalize / canReschedule → admin OR originator OR responsible OR actor (user OR team)
 *   - canEscalate                            → admin OR originator OR responsible OR participant
 *   - canApprove                             → admin OR approver
 *   - canDelete                              → admin OR originator
 {@link isSiteRestrictedAfterEscalation}.
 *
 * An "actor" means either an entry in `userOwners` matching the current user,
 * OR a team in `teamOwners` the current user belongs to. The team-side check
 * relies on the current user's team list exposed by the user service (see
 * {@link getCurrentUserTeamIds}); modules that have not yet wired the team
 * payload into `UserService` simply get the user-only behaviour with no
 * code change required on this service.
 */
@Injectable({ providedIn: 'root' })
export class SvcActionPlanPermissionService {
  readonly #userService = inject(UserService);

  // ─────────────────────────── basic identity ─────────────────────────────

  /** Returns the current user's ID in uppercase. */
  getCurrentUserId(): string {
    return String(this.#userService.userId$ ?? '')
      .toUpperCase()
      .trim();
  }

  /** True when the current user has the admin role in any action-plan application. */
  isActionPlanAdmin(): boolean {
    return !!this.#userService.user?.isSolvaceAdmin || this.hasActionPlanAdminRole();
  }

  hasActionPlanAdminRole(): boolean {
    const user = this.#userService.user;
    const applicationIds = new Set([
      String(StaticApplicationId.actionPlanRevamp ?? '').toUpperCase(),
      String(StaticApplicationId.actionPlan ?? '').toUpperCase(),
      String(StaticApplicationId.actionPlanCore ?? '').toUpperCase(),
    ]);
    const roleNames = new Set(['administrador', 'administrator']);

    return (user?.roles ?? []).some((role: { applicationId?: string; roleName?: string }) => {
      const appId = String(role?.applicationId ?? '').toUpperCase();
      const roleName = String(role?.roleName ?? '').toLowerCase();
      return applicationIds.has(appId) && roleNames.has(roleName);
    });
  }

  /** True if the two GUIDs refer to the same user (case-insensitive). */
  isSameGuid(a: string, b?: string | null): boolean {
    return (
      !!a &&
      a ===
        String(b ?? '')
          .toUpperCase()
          .trim()
    );
  }

  // ─────────────────────────── role membership ────────────────────────────

  /** True when the current user is originator, responsible, or actor on the item (no admin bypass). */
  isCurrentUserOwnerOrParticipant(item: ActionPlanPermissionShape): boolean {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) return false;

    if (this.isSameGuid(currentUserId, item?.originatorUserId)) return true;
    if (this.isSameGuid(currentUserId, item?.responsibleUserId)) return true;

    if (this.getActorIds(item).some((id) => this.isSameGuid(currentUserId, id))) return true;

    return this.#isCurrentUserOnTeamOwner(item);
  }

  getActorIds(item: ActionPlanPermissionShape | null | undefined): string[] {
    return this.#dedupeNonEmpty([item?.userOwners, item?.actorUserIds, item?.actorsUserIds, item?.actors]);
  }

  /**
   * Numeric team ids assigned as plan actors. Mirrors {@link getActorIds}
   * semantics: deduplicates, drops `0`/`NaN`, returns an empty array when no
   * teams are attached. Consumed by the team-actor leg of
   * {@link isCurrentUserOwnerOrParticipant}.
   */
  getTeamOwnerIds(item: ActionPlanPermissionShape | null | undefined): number[] {
    if (!Array.isArray(item?.teamOwners)) return [];
    const seen = new Set<number>();
    const result: number[] = [];
    for (const entry of item.teamOwners) {
      const numericId = Number(entry);
      if (!Number.isFinite(numericId) || numericId === 0 || seen.has(numericId)) continue;
      seen.add(numericId);
      result.push(numericId);
    }
    return result;
  }

  /**
   * Numeric team ids the current user belongs to, read from the user payload
   * shipped by `UserService`. Returns an empty array when the payload does
   * not expose any team membership — in that case the team-actor leg is a
   * no-op and behaviour matches the legacy user-only check.
   *
   * The lookup is permissive on the source field name (`teams`, `teamIds`,
   * `userTeams`, or array of `{ teamId }` objects) so any backend that
   * eventually ships the user's teams works without further changes here.
   */
  getCurrentUserTeamIds(): number[] {
    const user = this.#userService.user as unknown as Record<string, unknown> | null | undefined;
    if (!user) return [];

    const candidates: unknown[] = [];
    const directLists = [user['teams'], user['teamIds'], user['userTeams']];
    for (const value of directLists) {
      if (Array.isArray(value)) candidates.push(...value);
    }

    const seen = new Set<number>();
    const result: number[] = [];
    for (const entry of candidates) {
      const numericId = this.#extractTeamId(entry);
      if (numericId === null || seen.has(numericId)) continue;
      seen.add(numericId);
      result.push(numericId);
    }
    return result;
  }

  getParticipantIds(item: ActionPlanPermissionShape | null | undefined): string[] {
    return this.#dedupeNonEmpty([item?.participantUserIds, item?.participantsUserIds, item?.participants]);
  }

  // ───────────────────────── site-scoped escalation gate ──────────────────

  /** Site currently selected by the user; null when unknown. */
  getCurrentUserSiteId(): number | null {
    const siteId = Number(this.#userService.user?.lastSiteId ?? 0);
    return Number.isFinite(siteId) && siteId > 0 ? siteId : null;
  }

  isSiteRestrictedAfterEscalation(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    const originSiteId = this.#resolveEscalationOriginSiteId(actionPlan);
    if (originSiteId === null) return false;

    const userSiteId = this.getCurrentUserSiteId();
    if (userSiteId === null) return false;

    return userSiteId !== originSiteId;
  }

  // ───────────────────────── action permissions ───────────────────────────

  /** Site-gated after escalation (no admin bypass); then admin OR originator / responsible / actor. */
  canEdit(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    if (this.isSiteRestrictedAfterEscalation(actionPlan)) return false;
    if (this.isActionPlanAdmin()) return true;
    return this.isCurrentUserOwnerOrParticipant(actionPlan ?? {});
  }

  /**
   * Same matrix as canEdit BUT also requires an assigned approver (the modal
   * enforces both gates — see `showFinalizeButton` in `svc-modal-action-plan`).
   *
   * Set `requireApprover: false` on the call site if you want only the role
   * check (rare — most flows that finalize need an approver downstream).
   */
  canFinalize(actionPlan: ActionPlanPermissionShape | null | undefined, options: { requireApprover?: boolean } = { requireApprover: true }): boolean {
    if (!this.canEdit(actionPlan)) return false;
    if (options.requireApprover && !this.hasAssignedApprover(actionPlan)) return false;
    return true;
  }

  /** True when the action plan has at least one approver assigned. */
  hasAssignedApprover(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    return this.getApproverIds(actionPlan).length > 0;
  }

  /**
   * Returns the deduplicated list of authorised approvers on the item, read
   * from `approverUsers`. Note: `approverUserId` (= who actually approved) is
   * deliberately NOT included here; permission rules care about who CAN
   * approve, not who has already approved.
   */
  getApproverIds(item: ActionPlanPermissionShape | null | undefined): string[] {
    return this.#dedupeNonEmpty([item?.approverUsers]);
  }

  /** Same matrix as canEdit. */
  canReschedule(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    return this.canEdit(actionPlan);
  }

  /** Same matrix as canEdit. */
  canReopen(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    return this.canEdit(actionPlan);
  }

  /** Site-gated after escalation (no admin bypass); then admin OR originator / responsible / participant. */
  canEscalate(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    if (this.isSiteRestrictedAfterEscalation(actionPlan)) return false;
    if (this.isActionPlanAdmin()) return true;

    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) return false;

    if (this.isSameGuid(currentUserId, actionPlan?.originatorUserId)) return true;
    if (this.isSameGuid(currentUserId, actionPlan?.responsibleUserId)) return true;

    return this.getParticipantIds(actionPlan).some((id) => this.isSameGuid(currentUserId, id));
  }

  /** Site-gated after escalation (no admin bypass); then admin OR one of the approvers. */
  canApprove(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    if (this.isSiteRestrictedAfterEscalation(actionPlan)) return false;
    if (this.isActionPlanAdmin()) return true;

    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) return false;

    return this.getApproverIds(actionPlan).some((id) => this.isSameGuid(currentUserId, id));
  }

  /** Site-gated after escalation (no admin bypass); then admin OR originator only. */
  canDelete(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    if (this.isSiteRestrictedAfterEscalation(actionPlan)) return false;
    if (this.isActionPlanAdmin()) return true;

    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) return false;

    return this.isSameGuid(currentUserId, actionPlan?.originatorUserId);
  }

  /** Site-gated after escalation (no admin bypass); then admin OR originator OR actor (no responsible). */
  canCancel(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    if (this.isSiteRestrictedAfterEscalation(actionPlan)) return false;
    if (this.isActionPlanAdmin()) return true;

    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) return false;

    if (this.isSameGuid(currentUserId, actionPlan?.originatorUserId)) return true;
    return this.getActorIds(actionPlan).some((id) => this.isSameGuid(currentUserId, id));
  }

  // ───────────────────────── checklist tab permissions ──────────────────
  // BUG-64279 / BUG-64260: the checklist tab follows its own gating that is
  // independent of the modal's add/edit/view mode. The matrix:
  //   - canModifyChecklist     → admin OR originator/responsible/actor, AND
  //                              plan not concluded
  //   - canToggleChecklistItem → same as canModifyChecklist (selecting an
  //                              item shares the same role matrix as editing
  //                              it; non-privileged users see readonly items
  //                              without checkboxes)
  //   - isPlanConcluded        → status is "completed"
  //
  // Frontend is the source of truth for these flags so the tab keeps working
  // even when the backend payload does not include the (optional) helper
  // fields `canAddChecklistItems` / `canToggleComplete`.

  /** True when the action plan's status maps to the "completed" terminal state. */
  isPlanConcluded(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    const statusId = Number(actionPlan?.statusId ?? 0);
    return SvcModalActionPlanStatusTypeById[statusId] === 'completed';
  }

  /** True when the action plan is waiting for approver action. */
  isPlanUnderApproval(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    const statusId = Number(actionPlan?.statusId ?? 0);
    return SvcModalActionPlanStatusTypeById[statusId] === 'approval';
  }

  /**
   * Shared "freeze the approver field + the Edit menu" rule. The approver
   * locks down on both the under-approval and the completed states; the
   * checklist, by product rule, stays open during under-approval and only
   * freezes on completed (see {@link canModifyChecklist}). Reopen moves the
   * plan back to `ontime`/`late` and restores everything.
   */
  isPlanReadonlyByStatus(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    if (!actionPlan) return false;
    return this.isPlanConcluded(actionPlan) || this.isPlanUnderApproval(actionPlan);
  }

  /**
   * True when the current user is allowed to add, edit or remove checklist
   * items on this plan — regardless of the modal's edit-mode state.
   *
   * Product rule: the checklist stays editable while the plan is waiting for
   * approval (status `approval`); only the terminal `completed` state freezes
   * it. The approver field follows a stricter rule — see
   * {@link canModifyApprover}.
   *
   * Pass `null`/`undefined` while the plan is being created (no id yet) to
   * get an optimistic `true`: the originator is the one creating it.
   */
  canModifyChecklist(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    if (!actionPlan) return true;
    if (this.isPlanConcluded(actionPlan)) return false;
    return this.canEdit(actionPlan);
  }

  /**
   * True when the current user can flip a checklist item's completed flag.
   * Currently shares the same role matrix as {@link canModifyChecklist}; kept
   * as a separate method so Product can split the two later (e.g. allow
   * assignees to toggle without editing) without touching the component.
   */
  canToggleChecklistItem(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    return this.canModifyChecklist(actionPlan);
  }

  /**
   * True when the current user can change the approver list on this plan.
   * Mirrors the checklist matrix on purpose — the two tabs freeze together
   * when the plan reaches an approval / completed state.
   */
  canModifyApprover(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    if (!actionPlan) return true;
    if (this.isPlanReadonlyByStatus(actionPlan)) return false;
    return this.canEdit(actionPlan);
  }

  /**
   * Gate for the "Edit" affordance in the modal's overflow menu. Once the
   * plan is in approval or completed status the menu hides the option so the
   * user can't enter edit-mode in the first place; reopening the plan
   * (status falls back to in-progress) restores it.
   */
  canSwitchToEditMode(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    if (!actionPlan) return false;
    if (this.isPlanReadonlyByStatus(actionPlan)) return false;
    return this.canEdit(actionPlan);
  }

  /**
   * Whether the system field is locked when editing/escalating. Locked when:
   *   - the system is marked internal, OR
   *   - the action plan carries an explicit lock flag, OR
   *   - the action plan came from another source application (cross-app).
   */
  isSystemLockedForEdit(actionPlan: ActionPlanPermissionShape | null | undefined): boolean {
    if (actionPlan?.isSystemInternal === true) return true;
    const explicit = actionPlan?.isSystemLockedForEdit ?? actionPlan?.lockSystemOnEdit;
    if (typeof explicit === 'boolean') return explicit;
    return !!actionPlan?.sourceApplicationId;
  }

  // ─────────────────────────── helpers ────────────────────────────────────

  /**
   * Origin site for the escalation gate, or `null` when the plan is not
   * cross-site escalated (no restriction applies).
   *
   * The plan's own `siteId` is authoritative — escalation never rewrites it.
   * Histories only prove the plan left that site: each entry records the site
   * the plan lived at WHEN that escalation happened, so a plan re-escalated at
   * the destination shows destination→destination entries (e.g. AB#68221 QA:
   * `siteId: 2` with only `3→3` histories) and the true origin can NOT be
   * derived from `oldSiteId`. Any history site differing from `siteId` marks
   * the plan as cross-site escalated.
   */
  #resolveEscalationOriginSiteId(actionPlan: ActionPlanPermissionShape | null | undefined): number | null {
    const histories = actionPlan?.escalationHistories;
    if (!Array.isArray(histories) || histories.length === 0) return null;

    const planSiteId = this.#toPositiveId(actionPlan?.siteId);
    if (planSiteId !== null) {
      const escalatedAcrossSites = histories.some((history) =>
        [this.#toPositiveId(history?.oldSiteId), this.#toPositiveId(history?.newSiteId)].some(
          (historySiteId) => historySiteId !== null && historySiteId !== planSiteId
        )
      );
      return escalatedAcrossSites ? planSiteId : null;
    }

    // Shapes without `siteId`: fall back to the earliest cross-site transition.
    let originHistory: SvcActionPlanEscalationHistory | null = null;
    for (const history of histories) {
      const oldSiteId = this.#toPositiveId(history?.oldSiteId);
      const newSiteId = this.#toPositiveId(history?.newSiteId);
      if (oldSiteId === null || newSiteId === null || oldSiteId === newSiteId) continue;

      if (originHistory == null || this.#toEpoch(history?.historyDate) < this.#toEpoch(originHistory.historyDate)) {
        originHistory = history;
      }
    }

    return this.#toPositiveId(originHistory?.oldSiteId);
  }

  #toPositiveId(value: number | null | undefined): number | null {
    const numericId = Number(value ?? 0);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }

  #toEpoch(value: string | null | undefined): number {
    const parsed = Date.parse(value ?? '');
    return Number.isFinite(parsed) ? parsed : 0;
  }

  #dedupeNonEmpty(sources: Array<string[] | null | undefined>): string[] {
    const found = sources.find((entry) => Array.isArray(entry));
    if (!Array.isArray(found)) return [];
    return Array.from(new Set(found.map((id) => String(id ?? '').trim()).filter((id) => !!id)));
  }

  /**
   * True when any team owning the plan matches a team the current user
   * belongs to. Short-circuits when either side is empty, so call sites
   * incurring no plan-side teams (default state today) pay only an Array
   * length check.
   */
  #isCurrentUserOnTeamOwner(item: ActionPlanPermissionShape | null | undefined): boolean {
    const planTeams = this.getTeamOwnerIds(item);
    if (!planTeams.length) return false;
    const userTeams = this.getCurrentUserTeamIds();
    if (!userTeams.length) return false;
    const userTeamSet = new Set(userTeams);
    return planTeams.some((teamId) => userTeamSet.has(teamId));
  }

  /**
   * Coerces a raw entry from the user-payload team list into a positive
   * integer team id. Accepts either a plain number/string or an object that
   * carries a `teamId` / `groupId` field (the `/Groups/names` legacy API
   * uses `groupId`; the paginated `/Team` uses `teamId`).
   */
  #extractTeamId(entry: unknown): number | null {
    if (entry === null || entry === undefined) return null;
    if (typeof entry === 'number' || typeof entry === 'string') {
      const numericId = Number(entry);
      return Number.isFinite(numericId) && numericId !== 0 ? numericId : null;
    }
    if (typeof entry === 'object') {
      const candidate = entry as Record<string, unknown>;
      const directId = candidate['teamId'] ?? candidate['groupId'] ?? candidate['id'] ?? candidate['value'];
      const numericId = Number(directId ?? Number.NaN);
      return Number.isFinite(numericId) && numericId !== 0 ? numericId : null;
    }
    return null;
  }
}
